import { PAYTABLE, type VideoPokerState } from '../../engine/games/videoPoker';
import { evaluateFive, FIVE_CARD_NAMES, type FiveCardHand } from '../../engine/poker';
import { money } from '../../format';
import { cardsIn, PlayingCard } from '../parts';
import { Burst, Controls, secondChanceFrame, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type TableProps } from './Frame';

/** Each replaced card turns over this far apart, left to right. */
const CARD_MS = 260;
const LOST_MS = 900;
const BANNER_MS = 1100;
const END_HOLD_MS = 300;

/** The machine's paytable, top to bottom, in two columns like the glass. */
const ROWS: FiveCardHand[] = ['royalFlush', 'straightFlush', 'fourOfAKind', 'fullHouse', 'flush', 'straight', 'threeOfAKind', 'twoPair', 'jacksOrBetter'];
const SHORT: Partial<Record<FiveCardHand, string>> = {
  royalFlush: 'Royal Flush',
  straightFlush: 'Str. Flush',
  fourOfAKind: '4 of a Kind',
  fullHouse: 'Full House',
  threeOfAKind: '3 of a Kind',
  jacksOrBetter: 'Jacks+',
};

/** The paytable on the machine's glass; the row you're holding (or just
 *  won) lights up. */
function Paytable({ hit, flash }: { hit: FiveCardHand | null; flash: boolean }) {
  return (
    <div className="vp-paytable" aria-label="Paytable, times your bet">
      {ROWS.map((h) => (
        <div key={h} className={`vp-pay-row${h === hit ? (flash ? ' hit flash' : ' hit') : ''}`}>
          <span>{SHORT[h] ?? FIVE_CARD_NAMES[h]}</span>
          <span>×{PAYTABLE[h]}</span>
        </div>
      ))}
    </div>
  );
}

export function VideoPokerTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const state = run.tables.videopoker?.state as VideoPokerState | undefined;
  const anim = snapshot.anim;
  const luck = secondChanceFrame(snapshot);
  // The dead hand a ★ second chance redrew (the cards on the frame's first line).
  const dead = luck ? cardsIn(luck.split('\n')[0] ?? '') : [];
  const hasDead = dead.length === 5;
  const replaced = state ? state.held.map((h, i) => (h ? -1 : i)).filter((i) => i >= 0) : [];
  const flipMs = (replaced.length + 1) * CARD_MS;
  const firstEnd = hasDead ? flipMs + LOST_MS + BANNER_MS : 0;
  const elapsed = useAnimClock(snapshot, actions, firstEnd + flipMs + END_HOLD_MS);
  const [burst, fading, setBurst] = useBurst();
  useLuckMoment(snapshot, elapsed, hasDead ? flipMs + LOST_MS : null, () => ({ what: 'A dead draw is drawn again.', saved: true }), setBurst);

  const bet = run.tables.videopoker?.bet ?? 0;
  const idle = !state || state.phase === 'idle';

  // During the draw: the dead hand first (if luck redrew it), then the real one,
  // each turning its replaced cards over one at a time.
  let hand = state?.hand ?? [];
  let turned = 5;
  if (state && anim && state.phase === 'done') {
    const inFirst = hasDead && elapsed < firstEnd;
    if (inFirst) hand = dead;
    const t = inFirst ? elapsed : elapsed - firstEnd;
    turned = Math.floor(t / CARD_MS);
  }
  const hidden = (i: number) => idle || (!!anim && state!.phase === 'done' && !state!.held[i] && replaced.indexOf(i) >= turned);
  const holding = state?.phase === 'hold' && !anim;
  const done = state?.phase === 'done' && !anim;
  const now = holding ? evaluateFive(state.hand) : null;
  const hit = holding ? (now !== 'nothing' ? now : null) : done && state.result !== 'nothing' ? (state.result ?? null) : null;
  const win = done ? state.returned : 0;

  let result: string | undefined;
  let tone: 'win' | 'loss' | '' = '';
  if (done) {
    const net = state.returned - state.bet;
    // The WIN readout shows what it paid; this line shows what it did to you.
    result = `Last hand: ${FIVE_CARD_NAMES[state.result ?? 'nothing']} · ${state.lucky ? '★ ' : ''}${net === 0 ? 'bet back' : `${net > 0 ? '+' : '−'}${money(Math.abs(net))}`}`;
    tone = net > 0 ? 'win' : net < 0 ? 'loss' : '';
  }

  const hold = (i: number) => {
    if (holding) actions.tableOption(`card${i}`);
  };

  let screenLine = '';
  if (idle) screenLine = 'Press DEAL';
  else if (holding) screenLine = now !== 'nothing' ? FIVE_CARD_NAMES[now!] : 'Hold cards, then DRAW';
  else if (done) screenLine = state.result && state.result !== 'nothing' ? `Winner! ${FIVE_CARD_NAMES[state.result]}` : 'Game over';

  return (
    <>
      <Stage className="vp-stage" onClick={anim ? actions.skip : undefined}>
        <Burst burst={burst} fading={fading} pending={false} />
        <div className="vp-machine">
          <div className="vp-marquee">Jacks or Better</div>
          <Paytable hit={hit} flash={done} />
          <div className="vp-screen">
            <div className="vp-cards">
              {Array.from({ length: 5 }, (_, i) => (
                <button key={i} className={`vp-card${state?.held[i] && !idle ? ' held' : ''}`} onClick={() => hold(i)} disabled={!holding} tabIndex={-1} aria-hidden="true">
                  <span className="vp-held">{state?.held[i] && !idle ? 'HELD' : ''}</span>
                  {/* Cards are numbers and the ace of spades is 0, so compare, never test truthiness. */}
                  {hidden(i) || hand[i] === undefined ? <PlayingCard hidden /> : <PlayingCard card={hand[i]} />}
                </button>
              ))}
            </div>
            <div className={`vp-screen-line${done && state.returned > 0 ? ' lit' : ''}`}>{screenLine}</div>
          </div>
          <div className="vp-holds">
            {Array.from({ length: 5 }, (_, i) => (
              <button
                key={i}
                className={`vp-hold${state?.held[i] && !idle ? ' on' : ''}`}
                onClick={() => hold(i)}
                disabled={!holding}
                aria-pressed={!!state?.held[i] && !idle}
                aria-label={`Hold card ${i + 1}`}
              >
                Hold
              </button>
            ))}
          </div>
          <div className="vp-leds">
            <span>BET <b>{money(bet)}</b></span>
            <span>WIN <b className={win > 0 ? 'lit' : ''}>{money(win)}</b></span>
          </div>
        </div>
        <StatusLine snapshot={snapshot} result={result} tone={tone} />
      </Stage>
      <Controls snapshot={snapshot} actions={actions} hide={['paytable', 'card0', 'card1', 'card2', 'card3', 'card4']} primaryId="draw" onAct={() => setBurst(null)} />
    </>
  );
}
